import { useSystemStore } from "components/PageHeader/systemStore";
import { trackEventGA, trackEventGTM } from "lib/amplitude";
import { getSubDomain } from "lib/host";
import { isBrowser } from "lib/ssr";
import { useAuthStore } from "modules/Auth/authStore";
import { useHomeStore } from "modules/Home/homeStore";
import { useLeaderboardTenant } from "modules/Tenant/homeTenantApi";
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";

export default function PoinChip() {
  const theme = useSystemStore((state) => state.theme);
  const { currentUser, loginState } = useAuthStore((state) => ({
    currentUser: state.currentUser,
    loginState: state.loginState,
  }));
  const subDomain = isBrowser() ? getSubDomain(window.location.hostname) : null;

  const router = useRouter();

  const [isShowed, setIsShowed] = useState(false);

  const useLeaderboard = useLeaderboardTenant();
  const [poinData, setPoinData] = useState(null);
  const [todayPoints, setTodayPoints] = useState(0);

  // Tambahkan loading state dan ref untuk tracking request
  const [isLoading, setIsLoading] = useState(false);
  const isFetchingRef = useRef(false);
  const lastFetchParamsRef = useRef(null);

  const getTodayDate = () => new Date().toISOString().split("T")[0];

  const newLessonComplete = useHomeStore((state) => state.newLessonComplete);
  const setNewLessonComplete = useHomeStore(
    (state) => state.setNewLessonComplete
  );

  // Optimisasi: fungsi helper untuk fetch data
  const fetchLeaderboardData = () => {
    if (isFetchingRef.current || isLoading || !currentUser?.id) {
      return;
    }

    const params = {
      slug: currentUser?.referral ?? subDomain,
      user_id: currentUser?.id,
    };

    // Check jika parameter sama dengan request sebelumnya
    const currentParamsString = JSON.stringify(params);
    if (lastFetchParamsRef.current === currentParamsString) {
      return;
    }

    isFetchingRef.current = true;
    setIsLoading(true);
    lastFetchParamsRef.current = currentParamsString;

    useLeaderboard.doFetch(params, {
      onSuccess: (data: any) => {
        if (data?.data) {
          const userLeaderboard = data.data.find(
            (item: any) => item.user_id === currentUser?.id
          );

          if (userLeaderboard) {
            setPoinData(userLeaderboard);
            calculateTodayPoints(userLeaderboard.points_this_month);
            setNewLessonComplete(false);
          }
        }
        // Reset loading state setelah success
        isFetchingRef.current = false;
        setIsLoading(false);
      },
      onError: () => {
        // Handle error dan reset loading state
        isFetchingRef.current = false;
        setIsLoading(false);
      },
    });
  };

  // Optimisasi: useEffect yang lebih spesifik
  useEffect(() => {
    // Hanya fetch jika user sudah login dan ada ID
    if (currentUser?.id) {
      fetchLeaderboardData();
    }
  }, [currentUser?.id]); // Hanya trigger ketika user ID berubah

  // Separate useEffect untuk newLessonComplete
  useEffect(() => {
    if (newLessonComplete && currentUser?.id && !isLoading) {
      fetchLeaderboardData();
    }
  }, [newLessonComplete]); // Hanya trigger ketika lesson complete

  const calculateTodayPoints = (currentTotalPoints: number) => {
    const today = getTodayDate();
    const savedData = JSON.parse(localStorage.getItem("daily_poin") || "{}");

    if (savedData.date !== today) {
      localStorage.setItem(
        "daily_poin",
        JSON.stringify({ date: today, baseline: currentTotalPoints || 0 })
      );
      setTodayPoints(0);
    } else {
      const pointsEarned =
        (currentTotalPoints || 0) - (savedData.baseline || 0);
      setTodayPoints(isNaN(pointsEarned) ? 0 : pointsEarned);
    }
  };

  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsShowed(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsShowed(false);
    }, 200);
  };

  return (
    <div className="relative">
      <div
        className={`flex items-center gap-[4px] text-purple60_to_dark10 bg-light_neutral10_to_dark_neutral50 rounded-[100px] px-[12px] py-[8px] cursor-pointer`}
        onMouseEnter={() => {
          trackEventGTM({
            event: "leaderboard_hover_a",
            property: {
              user_id: currentUser?.id ?? "",
              is_logged_in: currentUser ? true : false,
              page: router.pathname,
            }
          })

          trackEventGA({
            event: "leaderboard_hover_a",
            property: {
              user_id: currentUser?.id ?? "",
              is_logged_in: currentUser ? true : false,
              page: router.pathname,
            }
          })

          handleMouseEnter();
        }}
        onMouseLeave={handleMouseLeave}
      >
        <Image
          alt="star"
          src={"/images/star-poin.svg"}
          width={24}
          height={24}
        />
        <span
          className={`${
            theme === "dark" ? "text-[#fff]" : "text-[#5D586C]"
          } font-[500] text-[14px]`}
        >
          {poinData?.points_this_month} Poin
        </span>
      </div>

      {/* Show if hover */}
      {isShowed && (
        <div
          className="absolute min-w-[240px] flex flex-col items-center p-[16px] right-0 top-52 z-10 mt-2 origin-top-right rounded-md bg-white_to_dark20 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="mb-[8px]">
            <p
              className={`${
                theme === "dark" ? "text-[#fff]" : "text-[#5D586C]"
              } font-[900] text-[14px] whitespace-nowrap`}
            >
              Perolehan poin hari ini:{" "}
              <span className="text-[#EFB443] font-[900]">
                {todayPoints} Poin
              </span>
            </p>
          </div>

          <button
            onClick={() =>
              // @ts-ignore
              router.push("/leaderboard")
            }
            className="w-full bg-[#FDF1DA] text-[#68491A] py-[8px] px-[16px] rounded-[16px] flex items-center gap-[10px]"
          >
            <span className="text-[12px]">Cek peringkat leaderboard</span>
            <Image
              alt="arrow"
              src={"/images/arrow-notail.svg"}
              width={16}
              height={16}
            />
          </button>
        </div>
      )}
    </div>
  );
}
